import React, { useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select,
} from '@mui/material';
import { queueStatusEnum } from '../../../utils/enum';

interface BulkStatusDialogProps {
    /** Whether the dialog is visible. */
    open: boolean;
    /** Number of currently selected rows, shown in the dialog title. */
    selectedCount: number;
    /** Whether a save/bulk-update request is in flight. Disables the Save button. */
    loading: boolean;
    /** Called when the user dismisses the dialog without saving. */
    onClose: () => void;
    /**
     * Called when the user clicks Save.
     * Receives the chosen status id so the parent can decide whether to show
     * a description popup (Rejected) or a confirmation dialog (New with high attempt-count).
     * @param status - The selected queue status id.
     */
    onSave: (status: number) => void;
}

/**
 * Modal dialog that lets the user pick a new status to apply to all selected
 * queue-upload rows in bulk.
 *
 * State (the currently-selected status) is managed internally and reset to 0
 * each time the dialog opens, keeping the parent component lean.
 *
 * @example
 * <BulkStatusDialog
 *   open={open}
 *   selectedCount={5}
 *   loading={false}
 *   onClose={() => setOpen(false)}
 *   onSave={(status) => handleBulkSave(status)}
 * />
 */
const BulkStatusDialog: React.FC<BulkStatusDialogProps> = ({
    open,
    selectedCount,
    loading,
    onClose,
    onSave,
}) => {
    const [selectedStatus, setSelectedStatus] = useState<number>(0);

    const handleSave = () => {
        onSave(selectedStatus);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Update Status for {selectedCount} Selected Row(s)</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="bulk-status-label">Status</InputLabel>
                    <Select
                        labelId="bulk-status-label"
                        value={selectedStatus}
                        label="Status"
                        onChange={(e) => setSelectedStatus(e.target.value as number)}
                    >
                        {queueStatusEnum.map(item => (
                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={loading}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkStatusDialog;
